/**
 * lib/importStatus.js — one vocabulary for statement-import job status.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The commit RPC's terminal-success status for a PDF is `committed`
 * (hotfix_20260521_part3_commit_rpc_status_vocab.sql — `pdf → committed`,
 * `csv → completed`). The UI never learned that word, so a fully-imported
 * statement fell through every status map and rendered as a raw grey
 * "committed" chip with a loader icon, no row summary and no CTA — which
 * reads exactly like "stuck, nothing happened, nothing was imported"
 * (reported from production, 2026-08-20).
 *
 * Every surface that renders a parse_job MUST derive its state from here so
 * the vocabulary can only drift in one place.
 */
import { CheckCircle2, AlertTriangle, Loader2, X, ShieldCheck } from 'lucide-react';

export const ACTIVE_STATUSES    = new Set(['queued', 'processing', 'parsing', 'retrying']);
export const HELD_STATUSES      = new Set(['done', 'parsed', 'needs_review', 'ready', 'held']);
export const IMPORTED_STATUSES  = new Set(['committed', 'completed', 'imported', 'consumed']);
export const FAILED_STATUSES    = new Set(['failed', 'abandoned', 'expired']);
export const CANCELLED_STATUSES = new Set(['cancelled', 'discarded', 'deleted', 'aborted']);

/**
 * Row state, in the order the UI cares about:
 *   'failed' | 'cancelled' | 'purged' | 'imported' | 'held' | 'active'
 * An unknown status is treated as 'active' (something is still happening)
 * rather than silently rendering an actionless row.
 */
export function jobState(job) {
  const s = job?.status;
  if (FAILED_STATUSES.has(s))    return 'failed';
  if (CANCELLED_STATUSES.has(s)) return 'cancelled';
  if (IMPORTED_STATUSES.has(s) || job?.consumed_at) {
    return job?.is_purged ? 'purged' : 'imported';
  }
  if (HELD_STATUSES.has(s))   return 'held';
  return 'active';
}

/** How many transactions actually reached the ledger for this job. */
export function importedCount(job) {
  const cs = job?.commit_summary || {};
  return cs.verified_ledger_rows ?? cs.inserted ?? job?.result_summary?.count ?? 0;
}

/** How many transactions were read out of the file (pre-commit). */
export function readCount(job) {
  return job?.result_summary?.count ?? 0;
}

/**
 * Honest one-liner for an imported job. A commit that inserted zero rows
 * (every fingerprint already in the ledger) must say so rather than claim
 * "Imported 0 transactions".
 */
export function importedLabel(job) {
  const n = importedCount(job);
  if (n > 0) return `Imported ${n} transaction${n === 1 ? '' : 's'}`;
  if (job?.commit_summary) return 'No new transactions — every row was already in your ledger';
  return 'Imported';
}

/** Joins meta-line fragments, dropping empties so no dangling `·` is shown. */
export function metaLine(...parts) {
  return parts.filter(Boolean).join(' · ');
}

const GOLD = 'bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]';
const GREEN = 'bg-emerald-500/15 text-emerald-600';
const MUTED = 'bg-muted text-muted-foreground';

/**
 * Pill descriptor for a job. Driven by state first (so `committed` and
 * `completed` can never render as an unknown status again) with a couple of
 * status-specific labels layered on top for the in-progress phases.
 */
export function statusPill(job) {
  const state = jobState(job);
  if (state === 'purged')    return { label: 'Archived',  icon: ShieldCheck,   cls: MUTED };
  if (state === 'imported')  return { label: 'Imported',  icon: CheckCircle2,  cls: GREEN };
  if (state === 'failed')    return { label: 'Failed',    icon: AlertTriangle, cls: 'bg-destructive/15 text-destructive' };
  if (state === 'cancelled') return { label: 'Cancelled', icon: X,             cls: MUTED };
  if (state === 'held') {
    return job.status === 'needs_review'
      ? { label: 'Review', icon: AlertTriangle, cls: GOLD }
      : { label: 'Ready',  icon: CheckCircle2,  cls: GREEN };
  }
  if (job?.status === 'queued')   return { label: 'Queued',   icon: Loader2, cls: MUTED, spin: true };
  if (job?.status === 'retrying') return { label: 'Retrying', icon: Loader2, cls: GOLD,  spin: true };
  return { label: 'Reading', icon: Loader2, cls: GOLD, spin: true };
}
